/**
 * Doppelte Arbeitsschritte zu einem zusammenführen und Ordnungsänderungen
 * schreiben — reine Logik; die Datenbank kommt als Parameter herein
 * (Supabase: `zusammenfuehrenDb.ts`, im Test eine Attrappe).
 *
 * Grundsätze:
 *  - Zusammenführen heißt: Ziel = Vereinigung aller Schritte, die übrigen
 *    (Quellen) werden gelöscht. Nichts, was einer der Schritte druckt, darf
 *    dabei verloren gehen — auch nicht die Maßnahmen der Altdaten.
 *  - Altdaten: Am Schritt hängen `sige_gefaehrdungen` → `sige_schutzmassnahmen`.
 *    Der Druck nimmt `schritt.schutzmassnahmen` und fällt NUR bei leerer Liste
 *    auf die Maßnahmen der Gefährdungen zurück (`generatorSigePlan.ts`,
 *    `layoutZeile`). Bekommt ein Ziel ohne direkte Maßnahmen beim
 *    Zusammenführen welche, verschwänden seine gedruckten Altdaten-Maßnahmen —
 *    deshalb zählen je Schritt die WIRKSAMEN Maßnahmen (`vereinigeSchritte`).
 *  - Gefährdungen einer gelöschten Quelle verschwinden mit (FK). Sie werden
 *    vorher an das Ziel gehängt; gelöscht wird nur, wenn das nachweislich für
 *    ALLE geklappt hat. Ob RLS dieses UPDATE erlaubt, ist nicht belegt — ein
 *    gesperrtes Update meldet bei Supabase keinen Fehler, nur 0 Zeilen.
 *    Deshalb wird gezählt und verglichen (`fuehreZusammen`).
 *  - Reihenfolge der Schritte so, dass ein Abbruch an jeder Stelle nur die
 *    doppelten Zeilen stehen lässt, nie Inhalt verliert.
 */
import { ergaenzeDokumente } from '../../services/mitgeltendeDokumente';
import { Arbeitsschritt, Gefaehrdung, Schutzmassnahme } from './types';
import type { SchrittOrdnung } from './abschnittOrdnung';

/** Editierbare Felder eines Schritts — wie `SchrittForm` in ArbeitsschritteListe. */
export type SchrittFelder = Omit<Arbeitsschritt, 'id' | 'sige_projekt_id' | 'sortierung'>;

const fehlerText = (e: unknown): string =>
  e instanceof Error ? e.message : (e as { message?: string })?.message || String(e);

/** Text ohne Randleerraum; NULL aus der Datenbank wird ''. */
const text = (t: string | null | undefined): string => String(t ?? '').trim();

/** Dublettenregel der Listen: Leerraum zusammengezogen, Groß/Klein egal. */
const listenSchluessel = (t: string): string => String(t ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

/** Vereinigung mehrerer Listen in dieser Reihenfolge; Leeres fällt weg, die erste Schreibweise bleibt. */
const vereinigeListen = (listen: (readonly string[] | null | undefined)[]): string[] => {
  const out: string[] = [];
  const gesehen = new Set<string>();
  listen.forEach(l => (l || []).forEach(eintrag => {
    const k = listenSchluessel(eintrag);
    if (!k || gesehen.has(k)) return;
    gesehen.add(k);
    out.push(eintrag);
  }));
  return out;
};

/**
 * schrittId → Maßnahmen-Texte aus den Gefährdungen (Altdaten), nur Schritte
 * mit mind. einer. Reihenfolge wie im Druck: Gefährdung für Gefährdung.
 */
export const altMassnahmenAus = (
  gefaehrdungen: Record<string, Gefaehrdung[]>,
  massnahmen: Record<string, Schutzmassnahme[]>,
): Record<string, string[]> => {
  const out: Record<string, string[]> = {};
  Object.entries(gefaehrdungen).forEach(([schrittId, gefs]) => {
    const texte = (gefs || [])
      .flatMap(g => (massnahmen[g.id] || []).map(m => m.beschreibung))
      .filter(t => text(t));
    if (texte.length) out[schrittId] = texte;
  });
  return out;
};

/** Wirksame Maßnahmen eines Schritts: die direkten, sind die leer, die der Altdaten. */
const wirksameMassnahmen = (s: Arbeitsschritt, altMassnahmen: Record<string, string[]>): string[] =>
  s.schutzmassnahmen?.length ? s.schutzmassnahmen : (altMassnahmen[s.id] || []);

/** BG-BAU-Codes eines Schritts — Legacy-Spalte wie `openEdit` in ArbeitsschritteListe. */
const hinweisCodesVon = (s: Arbeitsschritt): string[] =>
  s.hinweis_codes?.length
    ? s.hinweis_codes
    : (s.hinweis_code || '').split(';').map(t => t.trim()).filter(t => t && t.toLowerCase() !== 'n.v.');

/** Wert des Ziels, wenn nicht leer, sonst der erste nicht leere der Quellen, sonst ''. */
const erstesGefuellt = (werte: (string | null | undefined)[]): string =>
  werte.map(text).find(Boolean) || '';

/**
 * Vereinigung von ziel + quellen (Reihenfolge: ziel zuerst, dann quellen wie
 * übergeben). Regeln:
 * - bezeichnung, hauptabschnitt: vom Ziel.
 * - schutzmassnahmen: Vereinigung der WIRKSAMEN Maßnahmen je Schritt (direkte;
 *   sind die leer, altMassnahmen[id]) — sonst gingen beim Zusammenführen die
 *   gedruckten Altdaten-Maßnahmen des Ziels verloren. Dubletten weg (trim,
 *   Leerraum zusammengezogen, Groß/Klein egal), erste Schreibweise bleibt.
 * - vorschriften: Vereinigung, gleiche Dublettenregel.
 * - mitgeltende_dokumente: ergaenzeDokumente(ziel, quelle) nacheinander.
 * - hinweis_codes: Vereinigung; je Schritt `hinweis_codes`, wenn leer aus
 *   `hinweis_code` (an ';' trennen, trim, 'n.v.' verwerfen).
 *   hinweis_code = codes.join('; ') oder ''.
 * - zeitraum_von: frühestes nicht leeres (ISO YYYY-MM-DD), zeitraum_bis:
 *   spätestes nicht leeres; keins → ''.
 * - kritisch: true, wenn irgendeiner kritisch.
 * - gewerk: verschiedene nicht leere (Vergleich getrimmt, Groß/Klein egal) mit
 *   ', ' verbunden, Reihenfolge des Auftretens; ein Wert mit Komma zählt als
 *   mehrere Gewerke.
 * - koordinierungsbedarf, zustaendig_erstellung/_kontrolle/_instandhaltung,
 *   kategorie, raeumliche_zuordnung: Wert des Ziels, wenn nicht leer, sonst
 *   erster nicht leerer der Quellen, sonst ''.
 * Rein, ändert keine Eingabe.
 */
export const vereinigeSchritte = (
  ziel: Arbeitsschritt,
  quellen: Arbeitsschritt[],
  altMassnahmen: Record<string, string[]> = {},
): SchrittFelder => {
  const alle = [ziel, ...quellen];
  const mitgeltende_dokumente = quellen.reduce(
    (acc, q) => ergaenzeDokumente(acc, q.mitgeltende_dokumente || []),
    [...(ziel.mitgeltende_dokumente || [])],
  );
  const hinweis_codes = vereinigeListen(alle.map(hinweisCodesVon));
  // ISO-Daten sortieren sich als Text richtig.
  const daten = (feld: 'zeitraum_von' | 'zeitraum_bis') => alle.map(s => text(s[feld])).filter(Boolean).sort();
  const von = daten('zeitraum_von');
  const bis = daten('zeitraum_bis');
  // Ein schon zusammengeführtes „Maurer, Zimmerer“ zählt als zwei Gewerke —
  // sonst wüchse es beim nächsten Zusammenführen zu „Maurer, Zimmerer, Zimmerer“.
  // Kommas in Klammern („Elektro (Fa. A, B)“) trennen nicht.
  const gewerke = vereinigeListen([alle.flatMap(s => text(s.gewerk).split(/,(?![^(]*\))/).map(text))]);
  const feld = (f: 'koordinierungsbedarf' | 'zustaendig_erstellung' | 'zustaendig_kontrolle'
    | 'zustaendig_instandhaltung' | 'kategorie' | 'raeumliche_zuordnung') => erstesGefuellt(alle.map(s => s[f]));
  return {
    bezeichnung: ziel.bezeichnung,
    hauptabschnitt: ziel.hauptabschnitt || '',
    gewerk: gewerke.join(', '),
    zeitraum_von: von[0] || '',
    zeitraum_bis: bis[bis.length - 1] || '',
    raeumliche_zuordnung: feld('raeumliche_zuordnung'),
    schutzmassnahmen: vereinigeListen(alle.map(s => wirksameMassnahmen(s, altMassnahmen))),
    vorschriften: vereinigeListen(alle.map(s => s.vorschriften)),
    mitgeltende_dokumente,
    hinweis_codes,
    hinweis_code: hinweis_codes.join('; '),
    kritisch: alle.some(s => !!s.kritisch),
    kategorie: feld('kategorie'),
    koordinierungsbedarf: feld('koordinierungsbedarf'),
    zustaendig_erstellung: feld('zustaendig_erstellung'),
    zustaendig_kontrolle: feld('zustaendig_kontrolle'),
    zustaendig_instandhaltung: feld('zustaendig_instandhaltung'),
  };
};

// ─── Zusammenführen schreiben ──────────────────────────────────────────────

/** Datenbankzugriff des Zusammenführens — in der App Supabase, im Test eine Attrappe. */
export interface ZusammenfuehrDb {
  /**
   * Zielzeile schreiben (Felder komplett), liefert die gespeicherte Zeile.
   * Mit `vorher`: im selben Auftrag der Schreib-Warteschlange erst frisch
   * lesen und bei Abweichung `SchrittKonflikt` werfen, statt zu schreiben.
   */
  schreibeZiel: (id: string, felder: Record<string, unknown>, vorher?: Arbeitsschritt) => Promise<Arbeitsschritt>;
  /** Anzahl Gefährdungen an diesen Schritten. */
  zaehleGefaehrdungen: (schrittIds: string[]) => Promise<number>;
  /** Gefährdungen der Schritte `vonIds` an `nachId` hängen; liefert die Zahl tatsächlich geänderter Zeilen. */
  haengeGefaehrdungenUm: (vonIds: string[], nachId: string) => Promise<number>;
  /** Mit `vorher` wie bei `schreibeZiel`: erst frisch lesen, bei Abweichung `SchrittKonflikt`. */
  loescheSchritt: (id: string, vorher?: Arbeitsschritt) => Promise<void>;
  /** Frische Zeile für die Konfliktprüfung (null = existiert nicht mehr). */
  lese?: (id: string) => Promise<Arbeitsschritt | null>;
}

/**
 * Die Zeile weicht vom Stand ab, aus dem der Dialog vereinigt wurde — ein
 * anderer Tab, ein Kollege oder ein früherer, doch angekommener Versuch hat
 * sie geändert oder gelöscht.
 */
export class SchrittKonflikt extends Error {}

/**
 * Prüft die frische Zeile gegen `vorher` und wirft `SchrittKonflikt` bei
 * Abweichung. Für die Adapter: im selben Warteschlangen-Auftrag direkt vor dem
 * Schreiben/Löschen aufrufen — eine Versionsspalte, mit der die Datenbank das
 * selbst prüfen könnte, hat `sige_arbeitsschritte` nicht.
 */
export const pruefeUnveraendert = (vorher: Arbeitsschritt, frisch: Arbeitsschritt | null): void => {
  const name = vorher.bezeichnung || frisch?.bezeichnung || 'Ein Schritt';
  if (!frisch) throw new SchrittKonflikt(`„${name}“ existiert nicht mehr.`);
  if (schrittGeaendert(vorher, frisch)) throw new SchrittKonflikt(`„${name}“ wurde inzwischen geändert.`);
};

/**
 * Spalten, die der Bearbeiten-Dialog nicht kennt (`formToDb` schreibt sie
 * nicht), deren vereinigter Wert aber sonst mit der gelöschten Quelle
 * verschwände — `raeumliche_zuordnung` lesen Handbuch und Koordinierung.
 * Nur Spalten, die die frisch gelesene Zielzeile wirklich trägt: ob es sie in
 * jeder Umgebung gibt, belegt kein SQL im Repo, und ein Update auf eine
 * fehlende Spalte schlüge ganz fehl.
 */
export const zusatzFelder = (vereint: SchrittFelder, zeile: Arbeitsschritt): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  (['raeumliche_zuordnung', 'kategorie'] as const).forEach(f => {
    if (f in zeile) out[f] = text(vereint[f]) || null;
  });
  return out;
};

/** Spalten, deren Änderung durch einen anderen Tab/Nutzer das Zusammenführen ungültig macht. */
const KONFLIKT_FELDER = [
  'bezeichnung', 'gewerk', 'hauptabschnitt', 'zeitraum_von', 'zeitraum_bis', 'raeumliche_zuordnung',
  'schutzmassnahmen', 'vorschriften', 'mitgeltende_dokumente', 'hinweis_code', 'hinweis_codes', 'kritisch',
  'kategorie', 'koordinierungsbedarf', 'zustaendig_erstellung', 'zustaendig_kontrolle', 'zustaendig_instandhaltung',
] as const;

/** Vergleichswert einer Spalte: NULL, '' und [] gelten als gleich leer. */
const konfliktWert = (s: Arbeitsschritt, feld: (typeof KONFLIKT_FELDER)[number]): string => {
  const v = s[feld];
  if (Array.isArray(v)) return v.length ? JSON.stringify(v) : '';
  if (typeof v === 'boolean') return v ? '1' : '';
  return String(v ?? '').trim();
};

/** Ob `neu` in einer inhaltlichen Spalte von `alt` abweicht. Rein. */
export const schrittGeaendert = (alt: Arbeitsschritt, neu: Arbeitsschritt): boolean =>
  KONFLIKT_FELDER.some(f => konfliktWert(alt, f) !== konfliktWert(neu, f));

/** 'konflikt': eine Zeile wurde seit dem Öffnen des Dialogs geändert — nichts geschrieben, neu vereinigen. */
export type ZusammenfuehrStufe = 'pruefung' | 'konflikt' | 'ziel' | 'gefaehrdungen' | 'loeschen';

export interface ZusammenfuehrErgebnis {
  ziel: Arbeitsschritt | null;      // gespeicherte Zielzeile, null wenn Stufe 'ziel' scheiterte
  geloescht: string[];              // tatsächlich gelöschte Quell-ids
  umgehaengt: number;               // umgehängte Gefährdungen
  fehler: { stufe: ZusammenfuehrStufe; meldung: string } | null;
}

/** Warum die Auswahl nicht zusammengeführt werden kann — null = in Ordnung. */
const pruefeAuswahl = (zielId: string, quellIds: string[]): string | null => {
  if (!zielId) return 'Kein Ziel-Schritt gewählt.';
  if (!quellIds.length) return 'Keine Schritte zum Zusammenführen gewählt.';
  if (quellIds.some(id => !id)) return 'Ein gewählter Schritt hat keine Kennung.';
  if (quellIds.includes(zielId)) return 'Der Ziel-Schritt kann nicht mit sich selbst zusammengeführt werden.';
  if (new Set(quellIds).size !== quellIds.length) return 'Ein Schritt ist doppelt gewählt.';
  return null;
};

/** „1 doppelter Schritt“ / „3 doppelte Schritte“. */
const schritteText = (n: number): string => `${n} ${n === 1 ? 'doppelter Schritt' : 'doppelte Schritte'}`;

/**
 * Ablauf in dieser Reihenfolge, damit ein Abbruch nie Inhalt verliert:
 * 0. Prüfung: quellIds nicht leer, ohne zielId, ohne Doppelte; sonst fehler
 *    'pruefung', nichts geschrieben.
 *    Mit `stand` (die Zeilen, aus denen der Dialog vereinigt wurde) zusätzlich
 *    Konfliktprüfung (Codex-Review 02.11.2026): sonst überschriebe das Ziel
 *    die Änderung eines anderen Tabs/Kollegen, und das Löschen nähme eine
 *    inzwischen geänderte Quelle mit. Ziel und Quellen werden vorab parallel
 *    frisch gelesen; Abweichung → 'konflikt', nichts geschrieben. Ohne
 *    `db.lese` ist die Prüfung nicht möglich → ebenfalls 'konflikt'.
 *    Weil zwischen Vorprüfung und Schreiben noch Zeit vergeht, prüfen
 *    `schreibeZiel` und `loescheSchritt` mit `vorher` ein zweites Mal im
 *    selben Warteschlangen-Auftrag. Ganz schließen ließe sich das Fenster nur
 *    mit einer Versionsspalte (Migration).
 * 1. Ziel schreiben (`felder` z. B. formToDb des Dialogs). Konflikt →
 *    'konflikt', anderer Fehler → 'ziel'; beides Ende.
 * 2. Gefährdungen zählen; > 0 → umhängen; geänderte Zahl ≠ gezählte → fehler
 *    'gefaehrdungen' und KEIN Löschen — die Quellen hielten sonst Gefährdungen,
 *    die mit ihnen verschwänden.
 * 3. Quellen nacheinander löschen; beim ersten Fehler (auch Konflikt: die
 *    geänderte Quelle bleibt stehen) Ende, Meldung nennt, wie viele noch stehen.
 * Nie werfen — Fehler stehen im Ergebnis.
 */
export const fuehreZusammen = async (
  zielId: string,
  quellIds: string[],
  felder: Record<string, unknown>,
  db: ZusammenfuehrDb,
  stand?: Arbeitsschritt[],
): Promise<ZusammenfuehrErgebnis> => {
  const ergebnis: ZusammenfuehrErgebnis = { ziel: null, geloescht: [], umgehaengt: 0, fehler: null };

  const ungueltig = pruefeAuswahl(zielId, quellIds);
  if (ungueltig) return { ...ergebnis, fehler: { stufe: 'pruefung', meldung: ungueltig } };

  const vorherVon = (id: string): Arbeitsschritt | undefined => stand?.find(s => s.id === id);
  const neuVereinigen = 'Nichts gespeichert. „Weiter“ vereinigt erneut aus dem aktuellen Stand.';

  if (stand) {
    const lese = db.lese;
    const fehlt = [zielId, ...quellIds].find(id => !vorherVon(id));
    if (!lese || fehlt) {
      return { ...ergebnis, fehler: { stufe: 'konflikt', meldung: `Der Stand beim Öffnen des Dialogs ist unvollständig, eine Konfliktprüfung ist nicht möglich. ${neuVereinigen}` } };
    }
    try {
      const frisch = await Promise.all([zielId, ...quellIds].map(id => lese(id)));
      [zielId, ...quellIds].forEach((id, i) => pruefeUnveraendert(vorherVon(id)!, frisch[i]));
    } catch (e: unknown) {
      const konflikt = e instanceof SchrittKonflikt;
      return {
        ...ergebnis,
        fehler: konflikt
          ? { stufe: 'konflikt', meldung: `${fehlerText(e)} ${neuVereinigen}` }
          : { stufe: 'pruefung', meldung: `Aktueller Stand nicht lesbar: ${fehlerText(e)}. Nichts gespeichert.` },
      };
    }
  }

  try {
    const ziel = await db.schreibeZiel(zielId, felder, vorherVon(zielId));
    if (!ziel) throw new Error('Keine Zeile geändert (fehlende Berechtigung?)');
    ergebnis.ziel = ziel;
  } catch (e: unknown) {
    if (e instanceof SchrittKonflikt) return { ...ergebnis, fehler: { stufe: 'konflikt', meldung: `${fehlerText(e)} ${neuVereinigen}` } };
    return { ...ergebnis, fehler: { stufe: 'ziel', meldung: `Ziel-Schritt konnte nicht gespeichert werden: ${fehlerText(e)}` } };
  }

  const bleibenStehen = 'Die doppelten Schritte bleiben stehen.';
  try {
    const anzahl = await db.zaehleGefaehrdungen(quellIds);
    if (anzahl > 0) {
      ergebnis.umgehaengt = await db.haengeGefaehrdungenUm(quellIds, zielId);
      if (ergebnis.umgehaengt !== anzahl) {
        return {
          ...ergebnis,
          fehler: {
            stufe: 'gefaehrdungen',
            meldung: `Gefährdungen konnten nicht übernommen werden (${ergebnis.umgehaengt} von ${anzahl}). ${bleibenStehen} `
              + 'Möglicherweise fehlt die Schreibberechtigung.',
          },
        };
      }
    }
  } catch (e: unknown) {
    return {
      ...ergebnis,
      fehler: { stufe: 'gefaehrdungen', meldung: `Gefährdungen konnten nicht übernommen werden: ${fehlerText(e)}. ${bleibenStehen}` },
    };
  }

  for (const id of quellIds) {
    try {
      await db.loescheSchritt(id, vorherVon(id));
      ergebnis.geloescht.push(id);
    } catch (e: unknown) {
      const stehen = quellIds.length - ergebnis.geloescht.length;
      return {
        ...ergebnis,
        fehler: { stufe: 'loeschen', meldung: `Löschen fehlgeschlagen: ${fehlerText(e)}. ${schritteText(stehen)} ${stehen === 1 ? 'steht' : 'stehen'} noch.` },
      };
    }
  }
  return ergebnis;
};

// ─── Ordnung schreiben ─────────────────────────────────────────────────────

/** Datenbankzugriff für Ordnungsänderungen (`abschnittOrdnung.ts`). */
export interface OrdnungDb {
  schreibeOrdnung: (o: SchrittOrdnung) => Promise<void>;
}

/**
 * Schreibt Ordnungsänderungen nacheinander; ein Fehler bricht NICHT ab (wie
 * schreibeErgaenzungen in unterlagenErgaenzen.ts): er wird gesammelt, die
 * übrigen Schritte werden trotzdem verschoben. Nie werfen.
 */
export const schreibeOrdnung = async (
  ordnung: SchrittOrdnung[], db: OrdnungDb, fortschritt?: (erledigt: number, gesamt: number) => void,
): Promise<{ geschrieben: SchrittOrdnung[]; fehler: { id: string; meldung: string }[] }> => {
  const geschrieben: SchrittOrdnung[] = [];
  const fehler: { id: string; meldung: string }[] = [];
  for (let i = 0; i < ordnung.length; i++) {
    const o = ordnung[i];
    try {
      await db.schreibeOrdnung(o);
      geschrieben.push(o);
    } catch (e: unknown) {
      fehler.push({ id: o.id, meldung: fehlerText(e) });
    }
    fortschritt?.(i + 1, ordnung.length);
  }
  return { geschrieben, fehler };
};
